import {useEffect, useMemo, useRef, useState, useCallback} from 'react';
import {Box, Typography} from '@mui/material';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {useRenderThreadMap} from '../../../../../hooks/document/useDocument';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import './ThreadMap.scss';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import {ReactComponent as SenderIcon} from '../../../../../assets/images/sender.svg';
import {ReactComponent as ReceiverIcon} from '../../../../../assets/images/receiver.svg';
import {ReactComponent as LinkIcon} from '../../../../../assets/images/link-base-icon.svg';
import {ReactComponent as CompletedIcon} from '../../../../../assets/images/contextual-info-icon.svg';
import ArrCheckbox from '../../../../common/arr-checkbox/ArrCheckbox';
import {t} from 'i18next';
import CellWrapper from '../../../../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';
import {
  setDocAnalyticsCheckedDocsThreadMap,
  setDocAnalyticsPreviousId,
} from '../../../../../redux/slice/docViewSlice';
import {IThreadDocuments} from '../../../../../api/types/document.type';
import {getDocAnalyticsDocumentId} from '../../../docViewerUtils';

const NoRowsOverlayWithCustomLabel = ({
  noDataLabel,
}: {
  noDataLabel?: string;
}) => (
  <Box className="no-grid-overlay">
    <Typography
      className="typo-text body3_size"
      data-testid="no-meta-result"
      id="no-meta-result"
      tabIndex={0}
    >
      {noDataLabel ?? t('docExplorer.docDataGrid.noRowsOverlayCustomText')}
    </Typography>
  </Box>
);

const buildRows = (api: any, headerField = '__email__') => {
  if (!api) return {rows: [], documents: [], headerField};
  const documents: IThreadDocuments[] = api.threadDocuments || [];
  const participants = api.threadParticipants || [];

  const participantRows = participants.map((p: any) => {
    const row: Record<string, any> = {
      id: `participant:${p.emailID}`,
      __rowType: 'participant',
      [headerField]: p.emailID,
    };

    for (const doc of documents) {
      const match = p.participantDetails.find(
        (d: any) => d.documentId === doc.documentId
      );
      row[doc.docId] = match
        ? match.isRecipient
          ? t('docView.docAnalytics.recipient')
          : t('docView.docAnalytics.sender')
        : '';
    }
    return row;
  });

  const hasInclusive = documents?.some((d: any) => d.isInclusiveEmail);
  const inclusiveRow = hasInclusive
    ? (() => {
        const row: Record<string, any> = {
          id: '__inclusive__',
          __rowType: 'inclusive',
          [headerField]: '',
        };
        for (const doc of documents)
          row[doc.docId] = doc.isInclusiveEmail
            ? t('docView.docAnalytics.inclusive')
            : '';
        return row;
      })()
    : null;

  const checkboxRow: Record<string, any> = {
    id: '__checkbox__',
    __rowType: 'checkbox',
    [headerField]: t('docView.docAnalytics.selectAll'),
  };
  for (const doc of documents) checkboxRow[doc.docId] = '';

  return {
    rows: [inclusiveRow, checkboxRow, ...participantRows].filter(Boolean),
    documents,
    headerField,
  };
};

const RequestType = 'THREADEDDOCUMENTS';

const ThreadMap = ({
  width,
  noDataLabel,
}: {
  width: number;
  noDataLabel: string;
}) => {
  const activeDocumentObj = useSelector(
    (state: RootState) => state.docView.activeDoc
  );
  const {checkedDocsThreadMap: checkedDocsObjct, previouslySelectedDocId} =
    useSelector((state: RootState) => state.docView.docAnalytics);

  const checkedDocs = checkedDocsObjct.map((data) => data.docId);

  const codeSameAsDocs = useSelector(
    (state: RootState) => state.docView.codeSameAsDocs
  );
  const codeSameAsDocuments = codeSameAsDocs.map((data) =>
    data.documentId.toString()
  );
  const completedDocs = useSelector(
    (state: RootState) => state.docView.completedDocs
  );

  const docAnalyticsRefreshKey = useSelector(
    (state: RootState) => state.docView.docAnalyticsRefreshKey
  );
  const sourceId = useSelector((state: RootState) => state.docView.sourceId);

  const [data, setData] = useState<any | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [hoverColField, setHoverColField] = useState<string | null>(null);
  const activeDocumentId = activeDocumentObj?.documentId;
  const documentId = getDocAnalyticsDocumentId(
    activeDocumentId,
    previouslySelectedDocId
  );

  const dispatch = useDispatch();

  const {mutate: renderThreadMap, isPending: threadMapLoading} =
    useRenderThreadMap();

  useEffect(() => {
    if (!documentId) return;
    const currentId = documentId.toString();
    if (previouslySelectedDocId && previouslySelectedDocId !== currentId) {
      dispatch(setDocAnalyticsCheckedDocsThreadMap([]));
    }
    dispatch(setDocAnalyticsPreviousId(currentId));
  }, [documentId, previouslySelectedDocId, dispatch]);

  useEffect(() => {
    if (!documentId) return;
    const payload = {
      requestType: RequestType,
      direction: '',
      documentId: Number(documentId),
      pageNumber: 0,
      displayLength: 0,
      sortingColumn: '',
      maxRows: 0,
      maxCols: 0,
      sourceID: 0,
      assignmentId: sourceId ?? 0,
      isPopupWindow: false,
      docsForCodeSame: codeSameAsDocuments,
    };

    renderThreadMap(payload, {
      onSuccess: setData,
      onError: (err) => console.error('Error rendering thread map:', err),
    });
  }, [documentId, renderThreadMap, codeSameAsDocs, docAnalyticsRefreshKey]);

  const {rows, documents, headerField} = useMemo(() => buildRows(data), [data]);

  const allChecked =
    documents.length > 0 &&
    documents.every((doc: any) => checkedDocs.includes(doc.docId));

  const isIndeterminate =
    checkedDocs.length > 0 &&
    !allChecked &&
    checkedDocs.length < documents.length;

  const toggleDoc = useCallback(
    (docId: string) => {
      const updated = checkedDocs.includes(docId)
        ? checkedDocs.filter((id) => id !== docId)
        : [...checkedDocs, docId];
      const objectPayload = documents.filter((data) =>
        updated.includes(data.docId)
      );
      const completedDocsDocIds = new Set(
        completedDocs.map((item) => item.docId)
      );
      objectPayload.forEach((item) => {
        if (completedDocsDocIds.has(item.docId)) {
          item.isCompleted = true;
        }
      });
      dispatch(setDocAnalyticsCheckedDocsThreadMap(objectPayload));
    },
    [checkedDocs]
  );

  const toggleAll = useCallback(() => {
    const updated = allChecked ? [] : documents;
    const completedDocsDocIds = new Set(
      completedDocs.map((item) => item.docId)
    );
    updated.forEach((item) => {
      if (completedDocsDocIds.has(item.docId)) {
        item.isCompleted = true;
      }
    });
    dispatch(setDocAnalyticsCheckedDocsThreadMap(updated));
  }, [documents, allChecked]);

  const renderCellItems = (params: GridRenderCellParams) => {
    const rowType = params.row.__rowType as string;
    if (rowType === 'checkbox') {
      return (
        <ArrCheckbox
          data-testid={`checkbox-${params.field}`}
          aria-label={`cb-${params.id}-${params.field}`}
          id={`checkbox-${params.field}`}
          name={`checkbox-${params.field}`}
          size="small"
          checked={checkedDocs.includes(params.field)}
          onChange={() => toggleDoc(params.field)}
        />
      );
    }
    if (rowType === 'inclusive') {
      return params.value === t('docView.docAnalytics.inclusive')
        ? params.value
        : '';
    }
    return params.value == t('docView.docAnalytics.recipient') ? (
      <ReceiverIcon className="receiver-icon" data-testid="receiver-icon" />
    ) : params.value == t('docView.docAnalytics.sender') ? (
      <SenderIcon data-testid="sender-icon" />
    ) : (
      ''
    );
  };

  const getColumnClass = (
    col: any,
    hoverColField: string,
    type: 'header' | 'cell'
  ): string => {
    const hoverClass = type === 'header' ? 'newhover-header' : 'newhover';

    if (col.isActive) {
      return 'current_active';
    }

    if (hoverColField === col.field && col.isMarkedForCodeSame) {
      return 'newhover-codesameas';
    }

    if (col.isMarkedForCodeSame) {
      return 'current_codeSameAs';
    }

    if (hoverColField === col.field) {
      return hoverClass;
    }

    return '';
  };

  const columns = useMemo(() => {
    const docCols: GridColDef[] = documents.map((doc: any) => {
      // Check if this document is in completedDocs
      const isDocCompleted = completedDocs?.some(
        (completedDoc) => completedDoc.docId === doc.docId
      );

      return {
        field: doc.docId,
        headerName: doc.docId,
        sortable: false,
        filterable: false,
        isActive: doc.documentId == activeDocumentId,
        isCompleted: doc.isCompleted || isDocCompleted,
        isMarkedForCodeSame: doc.isMarkedForCodeSame,
        renderHeader: () => (
          <CellWrapper
            toolTipContent={doc.docId}
            dataTestId={`cell-wrapper-tooltip-content-threadmap-docid-${doc.docId}`}
          >
            <Box className="header-wrapper">
              {doc.docId}
              {(doc.isCompleted || isDocCompleted) && (
                <CompletedIcon
                  className="sender-cell-icon"
                  data-testid="completed-icon"
                />
              )}
              {doc?.isMarkedForCodeSame && <LinkIcon className="link-icon" />}
            </Box>
          </CellWrapper>
        ),
      };
    });

    return [
      {
        field: headerField,
        headerName: '',
        width: 224,
        sortable: false,
        filterable: false,
        cellClassName: 'transpose-row-header',
        renderCell: (params: GridRenderCellParams) => {
          const rowType = params.row.__rowType;
          if (rowType === 'checkbox') {
            return (
              <Box className="row-wrapper">
                <ArrCheckbox
                  data-testid={`checkbox-${params.field}`}
                  aria-label={`cb-${headerField}-${params.id}`}
                  id={`checkbox-${params.field}`}
                  name={`checkbox-${params.field}`}
                  size="small"
                  checked={allChecked || isIndeterminate}
                  isIndeterminate={isIndeterminate}
                  onChange={toggleAll}
                />
                {t('docView.docAnalytics.selectAll')}
              </Box>
            );
          }
          return (
            <CellWrapper
              toolTipContent={params.value}
              dataTestId={`cell-wrapper-tooltip-content-threadmap-${params.field}-${params.value}`}
              cellParams={params}
            >
              {params.value ?? ''}
            </CellWrapper>
          );
        },
        headerClassName: 'transpose-column-header',
        renderHeader: () => (
          <Box className="column-header-wrapper">
            <Box className="row-wrapper">
              <SenderIcon data-testid="sender-icon" />{' '}
              {t('docView.docAnalytics.sender')}
            </Box>
            <Box className="row-wrapper">
              <ReceiverIcon data-testid="receiver-icon" />{' '}
              {t('docView.docAnalytics.recipient')}
            </Box>
          </Box>
        ),
      },
      ...docCols.map((col) => {
        return {
          ...col,
          width:
            (col as any).isCompleted || (col as any).isMarkedForCodeSame
              ? 130
              : 110,
          minWidth:
            (col as any).isCompleted || (col as any).isMarkedForCodeSame
              ? 130
              : 110,
          headerAlign: 'center',
          headerClassName: getColumnClass(col, hoverColField ?? '', 'header'),

          rowSpanValueGetter: () => null,
          renderCell: (params: any) => {
            const handleEnter = () => {
              setHoverColField(params.field);
            };

            const handleLeave = () => {
              setHoverColField(null);
            };

            return (
              <Box
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
                className="render-cell-wrapper"
              >
                {renderCellItems(params)}
              </Box>
            );
          },
          cellClassName: getColumnClass(col, hoverColField ?? '', 'cell'),
        };
      }),
    ];
  }, [
    documents,
    checkedDocs,
    headerField,
    allChecked,
    toggleDoc,
    toggleAll,
    hoverColField,
    completedDocs,
    activeDocumentId,
  ]);

  return (
    <Box className="thread-map-container">
      {threadMapLoading ? (
        <Box className="loading-spinner-container">
          <ArrLoadingSpinner />
        </Box>
      ) : (
        <Box
          className="thread-map-data-grid"
          width={width}
          ref={gridRef}
          tabIndex={0}
        >
          {data && data.threadParticipants.length > 0 ? (
            <DataGridPro
              key={width}
              rows={rows}
              columns={columns as any}
              hideFooter
              rowSpanning={true}
              rowHeight={36}
              columnHeaderHeight={40}
              rowBufferPx={1500}
              columnBufferPx={8}
              disableColumnMenu
              disableColumnReorder
              disableColumnResize
              disableRowSelectionOnClick
              showColumnVerticalBorder
              showCellVerticalBorder
              getRowId={(row) => row.id}
              onColumnHeaderOver={(params) => {
                setHoverColField(params.field);
              }}
              onColumnHeaderOut={() => setHoverColField(null)}
            />
          ) : (
            <NoRowsOverlayWithCustomLabel noDataLabel={noDataLabel} />
          )}
        </Box>
      )}
    </Box>
  );
};

export default ThreadMap;
